import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Tooltip, Fab, Typography, IconButton } from '@mui/material';
import { Add } from '@mui/icons-material';
import { FormikProps } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { ExpertForm, Replacement } from '@ai/common/src/types/expert';
import FormFieldWrapper from '@ai/common/src/sections/expert-configuration/FormFieldWrapper';
import { cloneDeep } from 'lodash';
import { StyledTextField } from '@components/StyledTextField';

export default function PronunciationSettings({ form }: { form: FormikProps<ExpertForm> }) {
  const { t } = useTranslation();
  const { values, setFieldValue } = form;

  return (
    <FormFieldWrapper
      title={t('expertsList.pronunciation.title')}
      description={t('expertsList.pronunciation.descr')}
      fieldComponent={
        <Stack gap={2}>
          <SettingsBlock
            replacements={values.config.case_sensitive_replacements}
            setValue={(value) => setFieldValue('config.case_sensitive_replacements', value, true)}
            title={t('expertFormLabels.config.case_sensitive_replacements')}
            descr={t('expertsList.pronunciation.caseSensitiveDescr')}
          />

          <SettingsBlock
            replacements={values.config.case_insensitive_replacements}
            setValue={(value) => setFieldValue('config.case_insensitive_replacements', value, true)}
            title={t('expertFormLabels.config.case_insensitive_replacements')}
            descr={t('expertsList.pronunciation.caseInsensitiveDescr')}
          />
        </Stack>
      }
    />
  );
}

const TextFieldGroup = ({
  word,
  transcription,
  disabled = false,
  onDelete,
  onWordChange,
  onTranscriptionChange,
}: {
  word: string;
  transcription: string;
  disabled?: boolean;
  onDelete?: () => void;
  onWordChange?: (value: string) => void;
  onTranscriptionChange?: (value: string) => void;
}) => {
  const { t } = useTranslation();

  const isError = !word?.trim() && !!transcription?.trim();

  return (
    <Stack direction={'row'} gap={2} alignItems={'flex-start'}>
      <StyledTextField
        value={word}
        disabled={disabled}
        onChange={(e) => onWordChange?.(e.target.value)}
        error={isError}
        helperText={isError ? t('validation.requiredField') : ''}
        label={t('expertsList.pronunciation.word')}
      />

      <StyledTextField
        value={transcription}
        disabled={disabled}
        onChange={(e) => onTranscriptionChange?.(e.target.value)}
        error={isError}
        label={t('expertsList.pronunciation.pronunciation')}
      />

      <Tooltip title={t('delete')}>
        <IconButton size="small" color="primary" sx={{ width: 40, height: 40 }} onClick={onDelete}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const SettingsBlock = ({
  replacements,
  setValue,
  title,
  descr,
}: {
  replacements: Replacement[];
  setValue: (value: Replacement[]) => void;
  title: string;
  descr: string;
}) => {
  const { t } = useTranslation();

  const [replacementsToRender, setReplacementsToRender] = useState<Replacement[]>(
    replacements.length ? replacements : [['', ''], ...replacements],
  );

  return (
    <>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack flexGrow={1} maxWidth={'80%'}>
          <Typography fontWeight={700}>{title}</Typography>
          <Typography variant="body2" color="grey" whiteSpace={'pre-line'}>
            {descr}
          </Typography>
        </Stack>

        <Tooltip title={t('add')}>
          <Fab
            sx={{ flexShrink: 0 }}
            size="small"
            color="primary"
            onClick={() => {
              setReplacementsToRender((prev) => {
                const result: Replacement[] = [...prev, ['', '']];
                setValue(result);
                return result;
              });
            }}
          >
            <Add />
          </Fab>
        </Tooltip>
      </Stack>

      {replacementsToRender.map(([key, value], index) => (
        <TextFieldGroup
          key={index}
          word={key}
          onWordChange={(value) => {
            setReplacementsToRender((prev) => {
              const result = cloneDeep(prev);
              result[index][0] = value;
              setValue(result);
              return result;
            });
          }}
          transcription={value}
          onTranscriptionChange={(value) => {
            setReplacementsToRender((prev) => {
              const result = cloneDeep(prev);
              result[index][1] = value;
              setValue(result);
              return result;
            });
          }}
          onDelete={() => {
            setReplacementsToRender((prev) => {
              if (prev.length === 1 && !prev[0][0] && !prev[0][1]) {
                return prev;
              }

              const result = prev.filter((_, i) => i !== index);
              if (!result.length) {
                result.push(['', '']);
              }
              setValue(result);
              return result;
            });
          }}
        />
      ))}
    </>
  );
};
